import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import { trackPageView } from "@/services/ga4";

// Scroll to top on route change + fire GA4 page_view for SPA navigation.
// Single hook to avoid two listeners on the same useLocation.
function ScrollToTop() {
  const { pathname, search } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    trackPageView(pathname + search);
  }, [pathname, search]);
  return null;
}
import { AuthProvider } from "@/context/AuthContext";
import { SocketProvider } from "@/context/SocketContext";

// Pages — eager load landing page (first page users see)
import LandingPage from "@/pages/LandingPage";
import NotFoundPage from "@/pages/NotFoundPage";

// All other pages — lazy loaded (code split)
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const RegisterPage = lazy(() => import("@/pages/RegisterPage"));
const BrideFormPage = lazy(() => import("@/pages/BrideFormPage"));
const GroomFormPage = lazy(() => import("@/pages/GroomFormPage"));
const BrowseFormsPage = lazy(() => import("@/pages/BrowseFormsPage"));
const MessagesPage = lazy(() => import("@/pages/MessagesPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const ComplaintPage = lazy(() => import("@/pages/ComplaintPage"));
const RatingPage = lazy(() => import("@/pages/RatingPage"));
// Built (uses /my-tickets for both its own data fetch and its SEO path
// metadata) but never wired into the router — "support replied to your
// ticket" notifications pointed at /messages, where the reply doesn't
// exist, and InstallAppBanner.jsx already assumed /my-tickets was a real
// route for its own visibility logic.
const MyTicketsPage = lazy(() => import("@/pages/MyTicketsPage"));
const AssistantPage = lazy(() => import("@/pages/AssistantPage"));
const DailyPicksPage = lazy(() => import("@/pages/DailyPicksPage"));
const PrivacyPage = lazy(() => import("@/pages/PrivacyPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const FAQPage = lazy(() => import("@/pages/FAQPage"));
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const ProfileEditPage = lazy(() => import("@/pages/ProfileEditPage"));
const NotificationsPage = lazy(() => import("@/pages/NotificationsPage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const PreferenceSelectionPage = lazy(() => import("@/pages/PreferenceSelectionPage"));
const DataDeletionPage = lazy(() => import("@/pages/DataDeletionPage"));
const ChildSafetyPage = lazy(() => import("@/pages/ChildSafetyPage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const ImpressumPage = lazy(() => import("@/pages/ImpressumPage"));
const PublicCardPage = lazy(() => import("@/pages/PublicCardPage"));
const InvitePage = lazy(() => import("@/pages/InvitePage"));
const IdCapturePage = lazy(() => import("@/pages/IdCapturePage"));
const GuardianViewPage = lazy(() => import("@/pages/GuardianViewPage"));

// Admin Pages — lazy loaded
const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const UserManagement = lazy(() => import("@/pages/admin/UserManagement"));
const FormApproval = lazy(() => import("@/pages/admin/FormApproval"));
const IDVerifications = lazy(() => import("@/pages/admin/IDVerifications"));
const PhotoReview = lazy(() => import("@/pages/admin/PhotoReview"));
const ReportsManagement = lazy(() => import("@/pages/admin/ReportsManagement"));
const SupportChats = lazy(() => import("@/pages/admin/SupportChats"));
const TeamMessages = lazy(() => import("@/pages/admin/TeamMessages"));
const BlockManagement = lazy(() => import("@/pages/admin/BlockManagement"));
const ActivityLogs = lazy(() => import("@/pages/admin/ActivityLogs"));
const DeletedConversations = lazy(() => import("@/pages/admin/DeletedConversations"));
const IstikhataRejections = lazy(() => import("@/pages/admin/IstikhataRejections"));
const AdminBroadcasts = lazy(() => import("@/pages/admin/AdminBroadcasts"));
const SupportTickets = lazy(() => import("@/pages/admin/SupportTickets"));
const ContactMessages = lazy(() => import("@/pages/admin/ContactMessages"));
const DuplicateAccounts = lazy(() => import("@/pages/admin/DuplicateAccounts"));
const AdminFeedback = lazy(() => import("@/pages/admin/AdminFeedback"));
const AdminSuccessStories = lazy(() => import("@/pages/admin/AdminSuccessStories"));
const WhatsAppChats = lazy(() => import("@/pages/admin/WhatsAppChats"));
const WhatsAppCampaigns = lazy(() => import("@/pages/admin/WhatsAppCampaigns"));
const AdminBetaTesters = lazy(() => import("@/pages/admin/AdminBetaTesters"));
const BlogIndexPage = lazy(() => import("@/pages/BlogIndexPage"));
const SuccessStoriesPage = lazy(() => import("@/pages/SuccessStoriesPage"));
const BlogArticlePage = lazy(() => import("@/pages/BlogArticlePage"));
const GovernoratePage = lazy(() => import("@/pages/GovernoratePage"));
const CountryHubPage = lazy(() => import("@/pages/CountryHubPage"));
const CityPage = lazy(() => import("@/pages/CityPage"));
const DiasporaPage = lazy(() => import("@/pages/DiasporaPage"));
const ScheduledCallLandingPage = lazy(() => import("@/pages/ScheduledCallLandingPage"));
const ZawajHalalGuidePage = lazy(() => import("@/pages/ZawajHalalGuidePage"));

// Layout
import Layout from "@/components/Layout";
import ErrorBoundary from "@/components/ErrorBoundary";
import PrivateRoute from "@/components/PrivateRoute";

function App() {
  return (
    <HelmetProvider>
    <AuthProvider>
      <SocketProvider>
        <div className="App font-cairo">
          {/* Bottom, neutral, the type shown by the icon: how Gmail and the
              Material/iOS snackbar confirm an action. Mirrors mobile AppToast
              (2026-10-15); the top is where notifications arrive. */}
          <Toaster position="bottom-center" theme="dark" />
          <BrowserRouter>
            <ScrollToTop />
            <ErrorBoundary>
            <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" /></div>}>
            <Routes>
              <Route element={<Layout />}>
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
              <Route path="/bride-form" element={<PrivateRoute><BrideFormPage /></PrivateRoute>} />
              <Route path="/groom-form" element={<PrivateRoute><GroomFormPage /></PrivateRoute>} />
              <Route path="/browse" element={<BrowseFormsPage />} />
              <Route path="/card/:token" element={<PublicCardPage />} />
              <Route path="/invite/:code" element={<InvitePage />} />
              <Route path="/id-capture/:token" element={<IdCapturePage />} />
              <Route path="/guardian/view" element={<GuardianViewPage />} />
              <Route path="/messages" element={<PrivateRoute><MessagesPage /></PrivateRoute>} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/complaint" element={<PrivateRoute><ComplaintPage /></PrivateRoute>} />
              <Route path="/rating" element={<PrivateRoute><RatingPage /></PrivateRoute>} />
              <Route path="/my-tickets" element={<PrivateRoute><MyTicketsPage /></PrivateRoute>} />
              {/* Self-gating: the page redirects to /dashboard when the backend
                  is dormant, so the route existing costs nothing while off. */}
              <Route path="/assistant" element={<PrivateRoute><AssistantPage /></PrivateRoute>} />
              <Route path="/picks" element={<PrivateRoute><DailyPicksPage /></PrivateRoute>} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/data-deletion" element={<DataDeletionPage />} />
              <Route path="/child-safety" element={<ChildSafetyPage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/dashboard" element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
              <Route path="/profile/edit/:formId" element={<PrivateRoute><ProfileEditPage /></PrivateRoute>} />
              <Route path="/notifications" element={<PrivateRoute><NotificationsPage /></PrivateRoute>} />
              <Route path="/settings" element={<PrivateRoute><SettingsPage /></PrivateRoute>} />
              <Route path="/select-preference" element={<PrivateRoute><PreferenceSelectionPage /></PrivateRoute>} />

              <Route path="/about" element={<AboutPage />} />
              <Route path="/impressum" element={<ImpressumPage />} />

              {/* SEO: Blog and governorate landing pages */}
              <Route path="/blog" element={<BlogIndexPage />} />
              <Route path="/success-stories" element={<SuccessStoriesPage />} />
              <Route path="/blog/:slug" element={<BlogArticlePage />} />
              {/* Non-Egypt markets (Saudi / Morocco / Algeria) — purely additive.
                  The three hubs are STATIC paths, which React Router v6 ranks above
                  the dynamic /zawaj/:slug below it regardless of declaration order,
                  so /zawaj/cairo still resolves to GovernoratePage and no existing
                  URL moves. City pages are two-segment and cannot collide at all. */}
              <Route path="/zawaj/saudi" element={<CountryHubPage countrySlug="saudi" />} />
              <Route path="/zawaj/morocco" element={<CountryHubPage countrySlug="morocco" />} />
              <Route path="/zawaj/algeria" element={<CountryHubPage countrySlug="algeria" />} />
              <Route path="/zawaj/:country/:city" element={<CityPage />} />
              <Route path="/zawaj/:slug" element={<GovernoratePage />} />
              {/* React Router v6 cannot match a PARTIAL dynamic segment: in
                  `/muslim-marriage-:country` the `:country` is not a whole path
                  segment, so the route never matched and all three landings
                  fell through to `path="*"`. Googlebot read the pre-rendered
                  body while every human who clicked the result got "Page not
                  found" — invisible to the build, to the tests, and to any
                  check that fetches HTML without running it.
                  The URLs are indexed, so they stay exactly as they are and the
                  routes are listed one by one. `diaspora_routes_test` asserts
                  this list matches DIASPORA's own keys, so adding a country
                  cannot silently skip adding its route. */}
              <Route path="/muslim-marriage-uk" element={<DiasporaPage />} />
              <Route path="/muslim-marriage-germany" element={<DiasporaPage />} />
              <Route path="/muslim-marriage-usa" element={<DiasporaPage />} />
              <Route path="/mukalama-shareya" element={<ScheduledCallLandingPage />} />
              <Route path="/dalil-zawaj-halal" element={<ZawajHalalGuidePage />} />

              <Route path="*" element={<NotFoundPage />} />
            </Route>

            {/* Admin Routes (Phase 6) - No Layout wrapper */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/admin/users" element={<UserManagement />} />
            <Route path="/admin/forms/pending" element={<FormApproval />} />
            <Route path="/admin/id-verifications" element={<IDVerifications />} />
            <Route path="/admin/photo-review" element={<PhotoReview />} />
            <Route path="/admin/reports" element={<ReportsManagement />} />
            <Route path="/admin/support-chats" element={<SupportChats />} />
            <Route path="/admin/team-messages" element={<TeamMessages />} />
            <Route path="/admin/blocks" element={<BlockManagement />} />
            <Route path="/admin/deleted-conversations" element={<DeletedConversations />} />
            <Route path="/admin/istikhara-rejections" element={<IstikhataRejections />} />
            <Route path="/admin/broadcasts" element={<AdminBroadcasts />} />
            <Route path="/admin/support-tickets" element={<SupportTickets />} />
            <Route path="/admin/contact-messages" element={<ContactMessages />} />
            <Route path="/admin/duplicate-accounts" element={<DuplicateAccounts />} />
            <Route path="/admin/feedback" element={<AdminFeedback />} />
            <Route path="/admin/success-stories" element={<AdminSuccessStories />} />
            <Route path="/admin/whatsapp-chats" element={<WhatsAppChats />} />
            <Route path="/admin/whatsapp-campaigns" element={<WhatsAppCampaigns />} />
            <Route path="/admin/beta-testers" element={<AdminBetaTesters />} />
            <Route path="/admin/activity-logs" element={<ActivityLogs />} />
          </Routes>
            </Suspense>
            </ErrorBoundary>
          </BrowserRouter>
        </div>
      </SocketProvider>
    </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
